import { Event } from '@yandex-int/messenger.utils';
import { EventChannel, eventChannel } from 'redux-saga';

import { DrawingPerformanceStatsReport } from '../../../../../shared/helpers/drawing-performance-stats';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { Description, HaltDescriptor, OfferRequest } from '../types';

declare global {
    interface Window {
        ROOM_AGENT_sendDescription(params: Description): Promise<void>;
        ROOM_AGENT_halt(descriptor: HaltDescriptor): Promise<void>;
        ROOM_AGENT_ready(): Promise<void>;
        ROOM_AGENT_established(): Promise<void>;
        ROOM_AGENT_onStats(stats: DrawingPerformanceStatsReport): void;
        ROOM_AGENT_setViewport(viewport: { width: number; height: number }): Promise<void>;
        ROOM_AGENT_API: {
            setDescription(params: Description): Promise<void>;
            terminate(): Promise<void>;
            dtmfChar(char: string): void;
        };
    }
}

const logger = createLogger('room-agent-signaling-api');

function debugging() {
    logger.debug('initialized');

    window.ROOM_AGENT_sendDescription = (params: Description) => {
        logger.debug('sendDescription', params);

        return Promise.resolve();
    };

    window.ROOM_AGENT_ready = () => {
        logger.debug('ready');

        return Promise.resolve();
    };

    window.ROOM_AGENT_established = () => {
        logger.debug('established');

        return Promise.resolve();
    };

    window.ROOM_AGENT_halt = (descriptor: HaltDescriptor) => {
        logger.debug('halt', descriptor);

        return Promise.resolve();
    };

    window.ROOM_AGENT_onStats = (stats: DrawingPerformanceStatsReport) => {
        logger.debug('stats', stats);

        return Promise.resolve(stats);
    };

    window.ROOM_AGENT_setViewport = (viewport: { width: number; height: number }) => {
        logger.debug('setViewport', viewport);

        return Promise.resolve();
    };
}

export interface SetDescriptionEvent {
    data: Description | OfferRequest;
    resolve(): void;
    reject(error: unknown): void;
}

interface TerminateEvent {
    resolve(): void;
    reject(error: unknown): void;
}

export class SignalingAPI {
    public setDescriptionEventChannel: EventChannel<SetDescriptionEvent>;
    public terminateEventChannel: EventChannel<TerminateEvent>;
    public dtmfEvent = new Event<string>();
    constructor(debugMode?: boolean) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.ROOM_AGENT_API = {} as any;

        /**
         * Это single cast канал. Его следует использовать только в основной саге рум агента.
         */
        this.setDescriptionEventChannel = eventChannel<SetDescriptionEvent>((emit) => {
            let finished = false;

            window.ROOM_AGENT_API.setDescription = (data) => {
                if (finished) {
                    return Promise.reject(new Error('Event channel closed'));
                }

                return new Promise<void>((resolve, reject) => {
                    emit({
                        data,
                        resolve,
                        reject,
                    });
                });
            };

            return () => {
                finished = true;
            };
        });

        this.terminateEventChannel = eventChannel<TerminateEvent>((emit) => {
            let finished = false;

            window.ROOM_AGENT_API.terminate = () => {
                if (finished) {
                    return Promise.reject(new Error('Event channel closed'));
                }

                return new Promise<void>((resolve, reject) => {
                    emit({
                        resolve,
                        reject,
                    });
                });
            };

            return () => {
                finished = true;
            };
        });

        window.ROOM_AGENT_API.dtmfChar = (char) => {
            this.dtmfEvent.dispatch(char);
        };

        if (debugMode) {
            debugging();
        }
    }

    public sendDescription(params: Description) {
        logger.event('Sending description', {
            additional: {
                type: params.type,
                transformedLocalSdp: params.sdp,
                method: 'sendDescription',
            },
        });
        return window.ROOM_AGENT_sendDescription(params);
    }

    public ready() {
        return window.ROOM_AGENT_ready();
    }

    public established() {
        return window.ROOM_AGENT_established();
    }

    public halt(descriptor: HaltDescriptor) {
        logger.event('Sending halt signal', {
            additional: {
                reason: descriptor.reason,
                description: descriptor.description,
            },
        });
        return window.ROOM_AGENT_halt(descriptor);
    }

    public onStats(stats: DrawingPerformanceStatsReport) {
        return window.ROOM_AGENT_onStats?.(stats);
    }
}
